import { capitalizeFirstLetter } from '../../../../lib';
import getFormatMessage from '../../../../locales/get-format-message';
import {
  AppEnvType,
  AppName,
  AppRegionType,
  IAppConfigLegalTerms,
  IAppConfigLegalTermsProtocol,
  IAppCopyRightConfig,
  IAppDocumentConfig,
  IAppGameConfig,
  IString,
} from '../../../../model';

export const FORM_LAYOUT = {
  labelCol: { span: 4 },
  wrapperCol: { span: 20 },
};

export const LINE_FORM_LAYOUT = {
  labelCol: { span: 2 },
  wrapperCol: { span: 22 },
};

export const getEnvOptions = () => {
  return Object.values(AppEnvType).map((v: string) => {
    return { label: capitalizeFirstLetter(v), value: v };
  });
};

export const getRegionOptions = () => {
  return Object.values(AppRegionType).map((v) => {
    return { label: capitalizeFirstLetter(v), value: v };
  });
};

export const getAppNameOptions = () => {
  return Object.values(AppName).map((v) => {
    return { label: capitalizeFirstLetter(v), value: v };
  });
};

const isEmptyString = (val?: string) => {
  return typeof val === 'string' ? !val?.trim() : true;
};

const formatRequired = (keys: ReadonlyArray<string>) => {
  const suffix = keys.length > 1 ? 's' : '';
  const keyTips = keys.map((v) => `[${v}]`).join(',');
  return getFormatMessage('the_field_is_required', {
    params: {
      suffix,
      keyTips,
    },
  });
};

// 需要校验的简单数据结构的key, 复杂的数据结构校验用函数定义
const needValidCommonFields = [
  'env',
  'appID',
  'consoleURL',
  'userURL',
  'websiteURL',
  'cookieName',
];

const unNeedValidCommonFields = ['userID', 'aegisConfig'];

const commonValidator = (key: string, val: string): string | boolean => {
  if (needValidCommonFields.includes(key)) {
    return typeof val === 'string' && !!val.trim().length
      ? true
      : getFormatMessage('the_value_of_field_wrong', {
          params: {
            key,
          },
        });
  }
  return getFormatMessage('the_field_in_not_expected', {
    params: {
      key,
    },
  });
};

const JSONValidator: { [key: string]: Function } = {
  copyRight(obj: IAppCopyRightConfig) {
    const pass =
      !isEmptyString(obj?.copyRightText) &&
      obj?.protocolLinks &&
      Object.values(obj.protocolLinks).every((links) =>
        links.every((link) => link.label && link.url)
      );
    // const filingPass =
    //   (!!obj.filingCaseNumber && !!obj.filingLink) ||
    //   (!obj.filingCaseNumber && !obj.filingLink);
    return pass ? true : getFormatMessage('copyright_config_error');
  },
  gameConfig(obj: IAppGameConfig) {
    return obj?.region &&
      obj.region.length &&
      obj.region.every((v) => !isEmptyString(v[0]) && !isEmptyString(v[1]))
      ? true
      : getFormatMessage('game_config_error');
  },
  document(obj: IAppDocumentConfig) {
    const keys = Object.keys(obj.consoleHelperLinks);
    return keys.length &&
      keys.every((key) =>
        obj.consoleHelperLinks[key].every(
          (v) => !isEmptyString(v.label) && !isEmptyString(v.url)
        )
      )
      ? true
      : getFormatMessage('document_config_error');
  },
  // 用户中心注册法律条款
  legalTerms(obj: IAppConfigLegalTerms) {
    const validateLegalTermItem = (obj: IAppConfigLegalTerms) => {
      const keys = [
        'dataTransferProtocol',
        'privacyPolicyProtocol',
        'termsOfServiceProtocol',
      ];
      return keys.every((v: string) => {
        const objects = obj as unknown as IString<IAppConfigLegalTermsProtocol>;
        const config = objects[v];
        return (
          typeof config.label === 'string' &&
          typeof config.enabled === 'boolean' &&
          typeof config.url === 'string'
        );
      });
    };
    return 'enabledCookieTips' in obj &&
      typeof obj.enabledCookieTips === 'boolean' &&
      validateLegalTermItem(obj)
      ? true
      : getFormatMessage('legal_terms_config_error');
  },
};

export const JSONConfigFieldValidator = (obj: { [key: string]: any }) => {
  let validatePass: boolean | string = false;
  const needFields = needValidCommonFields.slice();
  const validator = Object.assign({}, JSONValidator);

  for (const key of Object.keys(obj)) {
    if (unNeedValidCommonFields.includes(key)) {
      continue;
    }
    const index = needFields.indexOf(key);
    if (index !== -1) {
      validatePass = commonValidator(key, obj[key]);
      if (typeof validatePass !== 'boolean') {
        return validatePass;
      }
      needFields.splice(index, 1);
    } else {
      const fn = validator[key];
      if (fn) {
        validatePass = validator[key](obj[key]);
        if (typeof validatePass !== 'boolean') {
          return validatePass;
        }
        delete validator[key];
      } else {
        if (JSONValidator[key]) {
          return getFormatMessage('duplicate_configuration_items', {
            params: {
              key,
            },
          });
        }
        // 没有配置的key
        return getFormatMessage('is_not_expected_field', {
          params: {
            key,
          },
        });
      }
    }
  }

  if (needFields.length || Object.keys(validator).length) {
    return formatRequired([...needFields, ...Object.keys(validator)]);
  }

  return false;
};
